import type { ReactElement } from 'react';
import type {
    ForensicRoleAssignmentReference,
    ForensicUserReference,
} from '@pillar-portal/api-types';

export interface ForensicUserDetailsProps {
    readonly label: string;
    readonly reference: ForensicUserReference;
}

export interface ForensicRoleDetailsProps {
    readonly roleAssignment: ForensicRoleAssignmentReference | null;
}

export interface ForensicNetworkDetailsProps {
    readonly ipAddress: string | null;
    readonly userAgent: string | null;
}

export function ForensicUserDetails({ label, reference }: ForensicUserDetailsProps): ReactElement {
    return (
        <div className="text-sm text-gray-700">
            <span className="font-medium">{label}:</span> {reference.primary_email}{' '}
            <span className="text-xs text-gray-500">({reference.user_uuid})</span>
        </div>
    );
}

export function ForensicRoleDetails({ roleAssignment }: ForensicRoleDetailsProps): ReactElement {
    if (roleAssignment === null) {
        return <div className="text-sm text-gray-600">Effective role: Unavailable</div>;
    }
    const organizationLabel: string =
        roleAssignment.organization === null
            ? 'Internal'
            : `${roleAssignment.organization.name} (${roleAssignment.organization.organization_uuid})`;

    return (
        <div className="rounded border bg-gray-50 p-2 text-sm text-gray-700">
            <div>
                <span className="font-medium">Effective role:</span> {roleAssignment.role}{' '}
                <span className="text-xs text-gray-500">
                    ({roleAssignment.role_assignment_uuid})
                </span>
            </div>
            <ForensicUserDetails label="Role owner" reference={roleAssignment.user} />
            <div>
                <span className="font-medium">Organization:</span> {organizationLabel}
            </div>
        </div>
    );
}

export function ForensicNetworkDetails({
    ipAddress,
    userAgent,
}: ForensicNetworkDetailsProps): ReactElement {
    return (
        <div className="text-xs text-gray-600">
            IP: {ipAddress ?? 'Unavailable'} · User agent: {userAgent ?? 'Unavailable'}
        </div>
    );
}
